import Highcharts from 'highcharts';
import { AppSchemasChartDeploymentComparison } from 'src/api/Api';
import allowedSpeciesList from 'src/view/tool/bioacoustic/components/speciesCatalog/data/species.json';

/**
 * Categorical series colours, validated (dataviz validate_palette.js, dark mode) against
 * the catalog card surface #0f1412: lightness band, chroma, CVD ΔE ≥ 8.4 and
 * normal-vision ΔE ≥ 19.3 for adjacent pairs, ≥ 3:1 contrast. Assign in this order and
 * never cycle past the last slot.
 */
export const SERIES_COLORS = [
  '#3987e5',
  '#d95926',
  '#199e70',
  '#c98500',
  '#d55181',
  '#008300',
  '#9085e9',
  '#e66767',
] as const;

/** Secondary encoding so overlaid deployments stay distinguishable without colour. */
export const SERIES_DASHES: Highcharts.DashStyleValue[] = [
  'Solid',
  'ShortDash',
  'ShortDot',
  'LongDash',
  'ShortDashDot',
];

export const MAX_COMPARED_DEPLOYMENTS = SERIES_DASHES.length;
export const MAX_SPECIES_SERIES = SERIES_COLORS.length;

export const UI = {
  accent: '#10B981',
  text: '#EDEDED',
  textSecondary: '#A3A3A3',
  textMuted: '#6B6B6B',
  border: '#1E2E25',
  hairline: '#1C2420',
  surface: '#0f1412',
  font: "'Century Gothic', Poppins, Arial, sans-serif",
};

export interface IndexConfig {
  key: 'aci' | 'bi' | 'adi' | 'aei' | 'ndsi';
  label: string;
  name: string;
  color: string;
  description: string;
}

export const INDEX_CONFIG: IndexConfig[] = [
  {
    key: 'aci',
    label: 'ACI',
    name: 'Acoustic Complexity Index',
    color: SERIES_COLORS[0],
    description: 'Soundscape complexity from amplitude variation between frequency bins. Higher usually means more biotic activity.',
  },
  {
    key: 'bi',
    label: 'BI',
    name: 'Bioacoustic Index',
    color: SERIES_COLORS[1],
    description: 'Area under the sound spectrum between 2 and 8 kHz, where most bird song sits.',
  },
  {
    key: 'adi',
    label: 'ADI',
    name: 'Acoustic Diversity Index',
    color: SERIES_COLORS[2],
    description: 'Shannon diversity of energy across frequency bands. Higher means sound is spread evenly across bands.',
  },
  {
    key: 'aei',
    label: 'AEI',
    name: 'Acoustic Evenness Index',
    color: SERIES_COLORS[3],
    description: 'Gini evenness of energy across frequency bins. Usually moves opposite to ADI.',
  },
  {
    key: 'ndsi',
    label: 'NDSI',
    name: 'Normalized Difference Soundscape Index',
    color: SERIES_COLORS[4],
    description: 'Biophony (2–8 kHz) versus anthrophony (1–2 kHz), from −1 (human noise) to +1 (natural sound).',
  },
];

export const toNumberArray = (raw: unknown): number[] => {
  const values = Array.isArray(raw)
    ? raw
    : raw && typeof raw === 'object' && Array.isArray((raw as { counts?: unknown }).counts)
      ? (raw as { counts: unknown[] }).counts
      : [];
  return values.map((v) => (typeof v === 'number' && !Number.isNaN(v) ? v : 0));
};

export const toNullableNumberArray = (raw: (number | null)[] | undefined): (number | null)[] =>
  (raw || []).map((v) => (typeof v === 'number' && !Number.isNaN(v) ? v : null));

export const sum = (values: number[]): number => values.reduce((acc, v) => acc + v, 0);

/** Min/max over every finite value in the given arrays. */
export const extent = (arrays: (number | null)[][]): [number, number] | null => {
  let min = Infinity;
  let max = -Infinity;
  arrays.forEach((arr) => arr.forEach((v) => {
    if (v === null) return;
    if (v < min) min = v;
    if (v > max) max = v;
  }));
  return min === Infinity ? null : [min, max];
};

/** Scale values to 0–1 against a shared extent so normalised panels stay comparable. */
export const normalizePoints = (
  values: (number | null)[],
  range: [number, number] | null,
): Highcharts.PointOptionsObject[] => values.map((v) => {
  if (v === null || !range) return { y: null };
  const [min, max] = range;
  const y = max > min ? (v - min) / (max - min) : 0.5;
  return { y: Number(y.toFixed(3)), custom: { raw: Number(v.toFixed(3)) } };
});

export interface SpeciesTrend {
  name: string;
  counts: number[];
  total: number;
}

/**
 * Strips away spaces, hyphens, underscores, punctuation, and all non-alphanumeric characters,
 * returning a clean lowercase alphanumeric string for consistent, resilient matching.
 */
export const stripSpeciesSymbols = (name: string): string => {
  return (name || '').toLowerCase().replace(/[^a-z0-9]/g, '');
};

/** Map of stripped alphanumeric lowercase name -> canonical species name from species.json */
const allowedSpeciesMap = new Map<string, string>();
(allowedSpeciesList as string[]).forEach((name) => {
  const canonical = name.trim();
  const stripped = stripSpeciesSymbols(canonical);
  if (stripped) {
    allowedSpeciesMap.set(stripped, canonical);
  }
});

/**
 * Returns canonical species name if allowed in species.json, or null if disallowed.
 * Strips away spaces and symbols from both the JSON list and the backend response to check the match,
 * so the same species matches regardless of formatting differences (e.g. "HouseCrow", "House Crow", "house_crow").
 * Handles direct matches as well as classifier compound labels (e.g. "Common Name_Scientific Name").
 */
export const getCanonicalSpeciesName = (speciesName: string): string | null => {
  if (!speciesName) return null;

  // 1. Direct match on stripped string
  const stripped = stripSpeciesSymbols(speciesName);
  const directMatch = allowedSpeciesMap.get(stripped);
  if (directMatch) {
    return directMatch;
  }

  // 2. Compound labels split by common separators (underscore, slash, pipe, parentheses)
  const parts = speciesName.split(/[_/|()]/).map((p) => p.trim()).filter(Boolean);
  for (const part of parts) {
    const strippedPart = stripSpeciesSymbols(part);
    const partMatch = allowedSpeciesMap.get(strippedPart);
    if (partMatch) {
      return partMatch;
    }
  }

  return null;
};

export const isSpeciesAllowed = (speciesName: string): boolean => {
  return getCanonicalSpeciesName(speciesName) !== null;
};

/** Species trends for one deployment, busiest first. Filtered strictly to catalog species in species.json. */
export const speciesTrendsOf = (comparison?: AppSchemasChartDeploymentComparison): SpeciesTrend[] => {
  const trendsByCanonicalName = new Map<string, number[]>();

  Object.entries(comparison?.activity_trend?.species_trends || {}).forEach(([rawName, raw]) => {
    const canonicalName = getCanonicalSpeciesName(rawName);
    if (!canonicalName) return;

    const counts = toNumberArray(raw);
    const existing = trendsByCanonicalName.get(canonicalName);
    if (!existing) {
      trendsByCanonicalName.set(canonicalName, counts);
    } else {
      const len = Math.max(existing.length, counts.length);
      const merged = Array.from({ length: len }, (_, i) => (existing[i] ?? 0) + (counts[i] ?? 0));
      trendsByCanonicalName.set(canonicalName, merged);
    }
  });

  return Array.from(trendsByCanonicalName.entries())
    .map(([name, counts]) => ({ name, counts, total: sum(counts) }))
    .sort((a, b) => b.total - a.total);
};

export const peakSlot = (counts: number[], timeSlots: string[]): string | null => {
  if (counts.length === 0) return null;
  let best = 0;
  counts.forEach((c, i) => { if (c > counts[best]) best = i; });
  return counts[best] > 0 ? timeSlots[best] ?? null : null;
};

interface BaseChartArgs {
  timeSlots: string[];
  yTitle: string;
  height?: number;
  legend?: boolean;
  normalized?: boolean;
  valueSuffix?: string;
}

/** Shared look for every catalog line chart: hairline grid, solid crosshair, readable 2h ticks. */
export const baseLineChartOptions = ({
  timeSlots,
  yTitle,
  height = 320,
  legend = true,
  normalized = false,
  valueSuffix = '',
}: BaseChartArgs): Highcharts.Options => ({
  chart: {
    type: 'line',
    backgroundColor: 'transparent',
    height,
    spacing: [12, 4, 8, 0],
    style: { fontFamily: UI.font },
  },
  exporting: { enabled: false },
  title: { text: undefined },
  credits: { enabled: false },
  accessibility: { enabled: false },
  xAxis: {
    categories: timeSlots,
    tickInterval: 4,
    labels: { style: { color: UI.textMuted, fontSize: '10px' } },
    lineColor: UI.hairline,
    tickLength: 0,
    crosshair: { color: 'rgba(255,255,255,0.12)', width: 1 },
  },
  yAxis: {
    title: {
      text: yTitle,
      style: { color: UI.textMuted, fontSize: '10px', fontWeight: '600' },
    },
    labels: {
      style: { color: UI.textMuted, fontSize: '10px' },
      ...(normalized ? { format: '{value:.1f}' } : {}),
    },
    gridLineColor: UI.hairline,
    gridLineWidth: 1,
    ...(normalized ? { min: 0, max: 1, tickInterval: 0.2 } : {}),
  },
  tooltip: {
    shared: true,
    backgroundColor: '#0B0F0D',
    borderColor: '#26332C',
    borderRadius: 8,
    padding: 10,
    style: { color: UI.text, fontSize: '11px' },
    headerFormat: '<span style="font-size:10px;color:#8A8A8A">{point.key}</span><br/>',
    pointFormatter: function (this: Highcharts.Point) {
      const raw = (this.options.custom as { raw?: number } | undefined)?.raw;
      const value = normalized && raw !== undefined
        ? `${this.y?.toFixed(2)} <span style="color:#8A8A8A">(raw ${raw})</span>`
        : `${this.y}${valueSuffix}`;
      return `<span style="color:${this.color}">●</span> ${this.series.name}: <b>${value}</b><br/>`;
    },
  },
  plotOptions: {
    series: {
      lineWidth: 2,
      animation: { duration: 300 },
      states: { hover: { lineWidthPlus: 1 }, inactive: { opacity: 0.25 } },
      marker: {
        enabled: false,
        radius: 4,
        lineWidth: 2,
        lineColor: UI.surface,
        states: { hover: { enabled: true } },
      },
    },
  },
  legend: {
    enabled: legend,
    align: 'left',
    verticalAlign: 'top',
    margin: 12,
    padding: 0,
    symbolWidth: 16,
    itemDistance: 16,
    itemStyle: { color: UI.textSecondary, fontSize: '11px', fontWeight: '500' },
    itemHoverStyle: { color: UI.text },
    itemHiddenStyle: { color: '#3A3A3A' },
  },
});
